/*
 * <twc-gift-redeem> — the /pages/redeem-gift-card flow.
 *
 * Rendered by sections/twc-redeem-gift-card.liquid (all copy lives there /
 * in the locale fragment; this file only toggles panels and fills amounts).
 *
 * Engine contract (proxy /apps/twc/v1, lanes L2/L3):
 *   POST /gift/check  {code}  -> {valid, amountCents, status}
 *   POST /gift/redeem {code [, tier, dobISO, termsAccepted]}
 * Both routes work for a LOGGED-IN customer with or without a membership
 * (unlike the dashboard 401). Logged out -> the CHECK press bounces to
 * /customer_authentication/login?return_to=<this page>, the sign-in form
 * every other surface uses (docs/decisions.md, header rules). The typed code
 * rides in ?code= on the return path and prefills the field on load; the
 * visitor presses Check again (no request wasted; the transport's own 401
 * bounce is the belt to these braces).
 *
 * Panels ([data-twc-panel]): code -> confirm (member: Liquid gate
 * data-has-member-row, the engine-projected member_since metafield = a
 * subscriber row exists, lapsed included) OR join (logged-in non-member:
 * tier + dd/mm/yyyy DOB + delivery address + terms) -> success (member/join
 * variants).
 *
 * Address search: the join panel's street field attaches the AddressFinder
 * widget through window.TwcAddressFinder (assets/twc-addressfinder.js) the
 * first time the panel opens. The section supplies the licence key in
 * data-addressfinder-key. A selection fills the address fields. With no key,
 * no adapter or a failed download, the fields stay manual.
 *
 * Transport = window.TwcAccountApi (same base + headers as every account
 * island). Every request disables its button while pending (rate-limit
 * friendly); errors render inline, engine 409 messages verbatim.
 */
(function (global) {
  'use strict';

  if (!global.customElements || global.customElements.get('twc-gift-redeem')) return;

  var DEFAULTS = {
    checking: 'Checking…',
    redeeming: 'Redeeming…',
    error_format: 'Enter the full code. It looks like TWC-XXXX-XXXX.',
    error_invalid: 'We cannot find that code. Check it and try again.',
    error_redeemed: 'This Gift Card has already been redeemed.',
    error_void: 'This Gift Card is no longer active.',
    error_generic: 'Something went wrong. Please try again.',
    tier_error: 'Choose your membership first.',
    dob_error: 'Enter your date of birth as dd/mm/yyyy.',
    dob_age_error: 'You must be 18 or over to join.',
    terms_error: 'Please accept the Terms and Conditions first.',
    address1_error: 'Enter your street address.',
    city_error: 'Enter your suburb.',
    // NZ store (SHOPIFY-1284): the town field is labelled City there.
    city_error_nz: 'Enter your city.',
    province_error: 'Choose your state.',
    postcode_error: 'Enter your 4-digit postcode.',
  };

  var CODE_RE = /^TWC-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
  var POSTCODE_RE = /^\d{4}$/;

  /* An AddressFinder selection arrives in the adapter's Shopify shape
   * {address1, address2, city, province, zip}. Each row names the key, the
   * join form field it fills and that field's error key. */
  var ADDRESS_FILL = [
    { key: 'address1', field: '[data-twc-address1]', error: 'address1' },
    { key: 'address2', field: '[data-twc-address2]', error: null },
    { key: 'city', field: '[data-twc-city]', error: 'city' },
    { key: 'province', field: '[data-twc-province]', error: 'province' },
    { key: 'zip', field: '[data-twc-postcode]', error: 'postcode' },
  ];

  /* Shopify's province codes for the two stores, the same list the Personal
   * Details address form holds (assets/twc-personal-details.js). The section
   * names the store's country on the select. */
  var PROVINCES = {
    AU: [
      ['ACT', 'Australian Capital Territory'],
      ['NSW', 'New South Wales'],
      ['NT', 'Northern Territory'],
      ['QLD', 'Queensland'],
      ['SA', 'South Australia'],
      ['TAS', 'Tasmania'],
      ['VIC', 'Victoria'],
      ['WA', 'Western Australia'],
    ],
    NZ: [
      ['AUK', 'Auckland'],
      ['BOP', 'Bay of Plenty'],
      ['CAN', 'Canterbury'],
      ['CIT', 'Chatham Islands'],
      ['GIS', 'Gisborne'],
      ['HKB', "Hawke's Bay"],
      ['MWT', 'Manawatu-Wanganui'],
      ['MBH', 'Marlborough'],
      ['NSN', 'Nelson'],
      ['NTL', 'Northland'],
      ['OTA', 'Otago'],
      ['STL', 'Southland'],
      ['TKI', 'Taranaki'],
      ['TAS', 'Tasman'],
      ['WKO', 'Waikato'],
      ['WGN', 'Wellington'],
      ['WTC', 'West Coast'],
    ],
  };

  function api() {
    return global.TwcAccountApi;
  }

  var Redeem = function () {
    return Reflect.construct(HTMLElement, [], Redeem);
  };
  Redeem.prototype = Object.create(HTMLElement.prototype);
  Redeem.prototype.constructor = Redeem;

  Redeem.prototype.connectedCallback = function () {
    var self = this;
    self._abort = new AbortController();
    var signal = self._abort.signal;

    self._i18n = DEFAULTS;
    var blob = self.querySelector('script[data-twc-i18n]');
    if (blob) {
      try {
        self._i18n = Object.assign({}, DEFAULTS, JSON.parse(blob.textContent));
      } catch (e) { /* defaults hold */ }
    }

    self._loggedIn = self.dataset.loggedIn === 'true';
    self._hasMemberRow = self.dataset.hasMemberRow === 'true';

    // The state list for the store's country, filled once.
    var provinceSelect = self.querySelector('[data-twc-province]');
    if (provinceSelect) {
      var provinces = PROVINCES[provinceSelect.dataset.twcCountry] || PROVINCES.AU;
      for (var p = 0; p < provinces.length; p++) {
        var option = document.createElement('option');
        option.value = provinces[p][0];
        option.textContent = provinces[p][1];
        provinceSelect.appendChild(option);
      }
    }
    self._busyOn = false;
    self._code = '';
    self._amountCents = null;
    self._addressFinderStarted = false;
    self._addressFinderWidget = null;

    var codeInput = self.querySelector('[data-twc-code]');
    if (codeInput) {
      // The return trip from sign-in carries the code the visitor typed
      // before the bounce. Prefill only — the visitor presses Check.
      if (!codeInput.value) {
        var carried = new URLSearchParams(global.location.search || '').get('code');
        if (carried) codeInput.value = self._maskCode(carried);
      }
      codeInput.addEventListener('input', function () {
        codeInput.value = self._maskCode(codeInput.value);
      }, { signal: signal });
      codeInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self._check(); }
      }, { signal: signal });
    }

    var dobInput = self.querySelector('[data-twc-dob]');
    if (dobInput) {
      dobInput.addEventListener('input', function () {
        dobInput.value = self._maskDob(dobInput.value);
      }, { signal: signal });
    }

    self.addEventListener('click', function (e) {
      if (e.target.closest('[data-twc-check]')) { self._check(); return; }
      var redeemBtn = e.target.closest('[data-twc-redeem]');
      if (redeemBtn) { self._redeem(redeemBtn); return; }
      if (e.target.closest('[data-twc-again]')) self._reset();
    }, { signal: signal });

    // The join form exists for browser autofill. A native submit never
    // reloads the page: it takes the same path as a redeem button press.
    self.addEventListener('submit', function (e) {
      e.preventDefault();
      var form = e.target;
      var submitBtn = form && form.querySelector ? form.querySelector('[data-twc-redeem]') : null;
      if (submitBtn) self._redeem(submitBtn);
    }, { signal: signal });
  };

  Redeem.prototype.disconnectedCallback = function () {
    if (this._abort) this._abort.abort();
    if (global.TwcAddressFinder) global.TwcAddressFinder.detach(this._addressFinderWidget);
    this._addressFinderWidget = null;
  };

  // "twc1234abcd" -> "TWC-1234-ABCD" as the member types. The mask only
  // uppercases and re-chunks (3-4-4, the code's own shape, prefix included) —
  // it never inserts characters the member did not type, so deletions and
  // mid-edits stay predictable and validation is the one honest gate.
  Redeem.prototype._maskCode = function (value) {
    var raw = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
    var parts = [];
    if (raw.length > 0) parts.push(raw.slice(0, 3));
    if (raw.length > 3) parts.push(raw.slice(3, 7));
    if (raw.length > 7) parts.push(raw.slice(7, 11));
    return parts.join('-');
  };

  // "20041991" -> "20/04/1991" as the member types.
  Redeem.prototype._maskDob = function (value) {
    var raw = String(value || '').replace(/[^0-9]/g, '').slice(0, 8);
    var out = raw.slice(0, 2);
    if (raw.length > 2) out += '/' + raw.slice(2, 4);
    if (raw.length > 4) out += '/' + raw.slice(4);
    return out;
  };

  // "20/04/1991" -> "1991-04-20", or null when not a real calendar date.
  Redeem.prototype._dobISO = function (value) {
    var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value || '').trim());
    if (!m) return null;
    var day = +m[1], month = +m[2], year = +m[3];
    if (month < 1 || month > 12 || day < 1 || year < 1900) return null;
    var daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day > daysInMonth) return null;
    return m[3] + '-' + m[2] + '-' + m[1];
  };

  // Age from calendar parts against today's local date — no instant math.
  Redeem.prototype._isAdult = function (iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return false;
    var now = new Date();
    var age = now.getFullYear() - +m[1];
    var monthDiff = now.getMonth() + 1 - +m[2];
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < +m[3])) age -= 1;
    return age >= 18;
  };

  // The sign-in form every other surface uses takes a RELATIVE return_to
  // (`/customer_authentication/login?return_to=`, the header's form). The
  // return path is this page plus the typed code, so nothing is lost.
  Redeem.prototype._loginBounceUrl = function (typed) {
    var login = this.dataset.loginUrl || '/customer_authentication/login';
    var returnTo = global.location.pathname;
    var code = this._maskCode(typed);
    if (code) returnTo += '?code=' + encodeURIComponent(code);
    var sep = login.indexOf('?') === -1 ? '?' : '&';
    return login + sep + 'return_to=' + encodeURIComponent(returnTo);
  };

  Redeem.prototype._panel = function (name) {
    return this.querySelector('[data-twc-panel="' + name + '"]');
  };

  Redeem.prototype._show = function (name) {
    Array.prototype.forEach.call(this.querySelectorAll('[data-twc-panel]'), function (panel) {
      panel.hidden = panel.dataset.twcPanel !== name;
    });
    if (name === 'join') this._attachAddressFinder();
    var panel = this._panel(name);
    if (panel) {
      var heading = panel.querySelector('h2') || panel;
      if (heading.focus) {
        heading.setAttribute('tabindex', '-1');
        try { heading.focus(); } catch (e) { /* ignore */ }
      }
    }
  };

  // The widget loads only when the join panel opens, and only once per
  // connection. The adapter returns null for a failed download, so a failure
  // leaves the manual fields as they are and logs nothing.
  Redeem.prototype._attachAddressFinder = function () {
    var self = this;
    if (self._addressFinderStarted) return;
    var adapter = global.TwcAddressFinder;
    var key = String(self.getAttribute('data-addressfinder-key') || '').trim();
    var panel = self._panel('join');
    var input = panel && panel.querySelector('[data-twc-address1]');
    if (!adapter || !key || !input) return;
    self._addressFinderStarted = true;

    var signal = self._abort && self._abort.signal;
    var province = panel.querySelector('[data-twc-province]');
    adapter.attach({
      input: input,
      // The widget appends its list inside this wrapper. The section CSS
      // puts the list directly below the input.
      container: input.closest('[data-twc-address-search]'),
      key: key,
      // The search and the state list use the same country.
      country: (province && province.dataset.twcCountry) || 'AU',
      onSelect: function (result) {
        if (signal && signal.aborted) return;
        self._applyAddress(panel, result);
      },
    }).then(function (widget) {
      // Loading is asynchronous. When the element left the page first,
      // disable the widget instead of keeping it.
      if (signal && signal.aborted) { adapter.detach(widget); return; }
      self._addressFinderWidget = widget;
    });
  };

  // Fill the join form from one AddressFinder selection. A field that now
  // passes its own check loses its error. A state with no matching option
  // keeps the current choice, and validation still decides on the press.
  Redeem.prototype._applyAddress = function (panel, result) {
    var self = this;
    if (!panel || !result) return;
    ADDRESS_FILL.forEach(function (row) {
      var input = panel.querySelector(row.field);
      if (!input) return;
      var value = String(result[row.key] == null ? '' : result[row.key]).trim();
      if (row.key === 'province') {
        var known = Array.prototype.some.call(input.options || [], function (option) {
          return option.value === value;
        });
        if (!value || !known) return;
      }
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      var valid = row.key === 'zip' ? POSTCODE_RE.test(value) : value !== '';
      if (row.error && valid) self._fieldError(panel, row.error, '', input);
    });
  };

  Redeem.prototype._error = function (line, message) {
    if (!line) return;
    line.textContent = message || '';
    line.hidden = !message;
  };

  Redeem.prototype._fieldError = function (panel, key, message, input) {
    this._error(panel.querySelector('[data-twc-error-for="' + key + '"]'), message);
    if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
  };

  Redeem.prototype._busy = function (btn, on, label) {
    this._busyOn = !!on;
    if (!btn) return;
    btn.disabled = !!on;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) {
      btn.dataset.twcLabel = btn.textContent;
      btn.textContent = label || '';
    } else if (btn.dataset.twcLabel) {
      btn.textContent = btn.dataset.twcLabel;
      delete btn.dataset.twcLabel;
    }
  };

  /* Currency: the member's engine currency (me.currency) when the member
   * store has loaded it; this page does not load the store, so in practice
   * the store's own currency from the section (data-twc-currency =
   * shop.currency) decides — NZD on the NZ store. AUD only when neither is
   * known (the behaviour before the NZ store). */
  Redeem.prototype._currency = function () {
    var md = global.TwcMemberData;
    var st = md && md.getState ? md.getState() : null;
    if (st && st.me && st.me.currency) return st.me.currency;
    return this.dataset.twcCurrency || 'AUD';
  };

  Redeem.prototype._money = function (cents) {
    var a = api();
    if (a && a.money) return a.money(cents, this._currency());
    return '$' + (cents / 100).toFixed(2);
  };

  Redeem.prototype._fillAmounts = function () {
    var money = this._amountCents != null ? this._money(this._amountCents) : '';
    Array.prototype.forEach.call(this.querySelectorAll('[data-twc-amount]'), function (el) {
      el.textContent = money;
    });
  };

  Redeem.prototype._check = function () {
    var self = this;
    if (self._busyOn) return;

    var panel = self._panel('code');
    var input = panel && panel.querySelector('[data-twc-code]');
    var errorLine = panel && panel.querySelector('[data-twc-check-error]');
    var btn = panel && panel.querySelector('[data-twc-check]');
    if (!input) return;

    // Logged out: the check cannot succeed — go sign in and come back here.
    if (!self._loggedIn) {
      global.location.href = self._loginBounceUrl(input.value);
      return;
    }

    var code = self._maskCode(input.value);
    input.value = code;
    if (!CODE_RE.test(code)) {
      self._error(errorLine, self._i18n.error_format);
      input.setAttribute('aria-invalid', 'true');
      return;
    }
    input.setAttribute('aria-invalid', 'false');
    self._error(errorLine, '');
    self._busy(btn, true, self._i18n.checking);

    api().post('/gift/check', { code: code }).then(function (r) {
      self._busy(btn, false);
      var d = r && r.ok ? r.data : null;
      if (d && d.valid) {
        self._code = code;
        self._amountCents = d.amountCents;
        self._fillAmounts();
        self._show(self._hasMemberRow ? 'confirm' : 'join');
        return;
      }
      if (d && d.status === 'redeemed') { self._error(errorLine, self._i18n.error_redeemed); return; }
      if (d && d.status === 'void') { self._error(errorLine, self._i18n.error_void); return; }
      if (d && !d.valid) { self._error(errorLine, self._i18n.error_invalid); return; }
      if (r && (r.code === 'NOT_FOUND' || r.code === 'VALIDATION_ERROR')) {
        self._error(errorLine, self._i18n.error_invalid);
        return;
      }
      // 401 already bounced via the transport; anything else is said honestly.
      self._error(errorLine, (r && r.message) || self._i18n.error_generic);
    });
  };

  Redeem.prototype._redeem = function (btn) {
    var self = this;
    if (self._busyOn || !self._code) return;

    var panel = btn.closest('[data-twc-panel]');
    if (!panel) return;
    var isJoin = panel.dataset.twcPanel === 'join';
    var errorLine = panel.querySelector('[data-twc-redeem-error]');
    var body = { code: self._code };

    if (isJoin) {
      var tierSelect = panel.querySelector('[data-twc-tier]');
      var dobInput = panel.querySelector('[data-twc-dob]');
      var terms = panel.querySelector('[data-twc-terms]');
      var bad = false;

      var tier = tierSelect && tierSelect.value;
      if (!tier) { self._fieldError(panel, 'tier', self._i18n.tier_error, tierSelect); bad = true; }
      else self._fieldError(panel, 'tier', '', tierSelect);

      var iso = self._dobISO(dobInput && dobInput.value);
      if (!iso) { self._fieldError(panel, 'dob', self._i18n.dob_error, dobInput); bad = true; }
      else if (!self._isAdult(iso)) { self._fieldError(panel, 'dob', self._i18n.dob_age_error, dobInput); bad = true; }
      else self._fieldError(panel, 'dob', '', dobInput);

      if (!terms || !terms.checked) { self._fieldError(panel, 'terms', self._i18n.terms_error); bad = true; }
      else self._fieldError(panel, 'terms', '');

      // The delivery address: the first box goes here, so every part the
      // warehouse needs is required before the membership is created.
      var address1 = panel.querySelector('[data-twc-address1]');
      var address2 = panel.querySelector('[data-twc-address2]');
      var city = panel.querySelector('[data-twc-city]');
      var province = panel.querySelector('[data-twc-province]');
      var postcode = panel.querySelector('[data-twc-postcode]');
      var street = ((address1 && address1.value) || '').trim();
      var suburb = ((city && city.value) || '').trim();
      var state = (province && province.value) || '';
      var post = ((postcode && postcode.value) || '').trim();
      if (!street) { self._fieldError(panel, 'address1', self._i18n.address1_error, address1); bad = true; }
      else self._fieldError(panel, 'address1', '', address1);
      // The form's country is the state list's (data-twc-country). In NZ the
      // town field is City and the region is optional (SHOPIFY-1284); an AU
      // address still needs its suburb and state.
      var isNz = !!province && province.dataset.twcCountry === 'NZ';
      var cityError = (isNz && self._i18n.city_error_nz) || self._i18n.city_error;
      if (!suburb) { self._fieldError(panel, 'city', cityError, city); bad = true; }
      else self._fieldError(panel, 'city', '', city);
      var needsState = !isNz;
      if (needsState && !state) { self._fieldError(panel, 'province', self._i18n.province_error, province); bad = true; }
      else self._fieldError(panel, 'province', '', province);
      if (!POSTCODE_RE.test(post)) { self._fieldError(panel, 'postcode', self._i18n.postcode_error, postcode); bad = true; }
      else self._fieldError(panel, 'postcode', '', postcode);

      if (bad) return;
      body.tier = tier;
      body.dobISO = iso;
      body.termsAccepted = true;
      body.address = {
        address1: street,
        address2: ((address2 && address2.value) || '').trim(),
        city: suburb,
        province: state,
        zip: post,
      };
    }

    self._error(errorLine, '');
    self._busy(btn, true, self._i18n.redeeming);

    api().post('/gift/redeem', body).then(function (r) {
      self._busy(btn, false);
      if (r && r.ok) {
        self._showSuccess(isJoin ? 'join' : 'member', r.data);
        return;
      }
      // 409 CONFLICT (someone else's card), VALIDATION_ERROR etc. carry
      // member-readable messages — render them verbatim.
      self._error(errorLine, (r && r.message) || self._i18n.error_generic);
    });
  };

  // The redeem response's balance key is rendered when present under any of
  // the shapes the engine may answer with; absent -> the balance line stays
  // hidden and the success copy still tells the truth.
  Redeem.prototype._showSuccess = function (variant, data) {
    var self = this;
    Array.prototype.forEach.call(self.querySelectorAll('[data-twc-success-title]'), function (el) {
      el.hidden = el.dataset.twcSuccessTitle !== variant;
    });
    Array.prototype.forEach.call(self.querySelectorAll('[data-twc-success-body]'), function (el) {
      el.hidden = el.dataset.twcSuccessBody !== variant;
    });

    var cents = null;
    if (data) {
      var candidates = [data.creditBalanceCents, data.newBalanceCents, data.balanceCents, data.giftCreditCents];
      for (var i = 0; i < candidates.length; i += 1) {
        if (typeof candidates[i] === 'number') { cents = candidates[i]; break; }
      }
      if (cents == null && data.giftCredit && typeof data.giftCredit.balanceCents === 'number') {
        cents = data.giftCredit.balanceCents;
      }
    }
    var line = self.querySelector('[data-twc-balance-line]');
    var value = self.querySelector('[data-twc-balance]');
    if (line) line.hidden = cents == null;
    if (value && cents != null) value.textContent = self._money(cents);

    self._show('success');
  };

  Redeem.prototype._reset = function () {
    this._code = '';
    this._amountCents = null;
    var input = this.querySelector('[data-twc-code]');
    if (input) input.value = '';
    // A join success just made this customer a member — the confirm panel is
    // the truthful next stop for another card.
    if (this.querySelector('[data-twc-success-body="join"]:not([hidden])')) {
      this._hasMemberRow = true;
    }
    this._show('code');
    if (input) {
      try { input.focus(); } catch (e) { /* ignore */ }
    }
  };

  global.customElements.define('twc-gift-redeem', Redeem);
})(window);
